/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies, consistent-return */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAlert, AlertType } from 'src/components/AlertsProvider';
import { useOmniverseConfig } from './useConfig';
import { getSession } from '../state/Sessions';

/**
 * Shows a notification when the session is about to end based on the configured max TTL.
 * @param sessionId
 */
export default function useStreamEndNotification(sessionId: string) {
  const config = useOmniverseConfig();
  const { showAlert } = useAlert();

  const { data: session } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: async () => getSession({ config, sessionId }),
    enabled: !!sessionId,
    refetchInterval: 60000, // Check every minute
  });

  useEffect(() => {
    if (!session) return;

    const sessionStartTime = session.startDate.getTime();
    const maxTtlMs = config.sessions.maxTtl * 1000;
    const notificationTime = config.sessions.sessionEndNotificationTime * 1000;
    const sessionEndTime = sessionStartTime + maxTtlMs;
    const notificationTriggerTime = sessionEndTime - notificationTime;

    const now = Date.now();
    const timeUntilNotification = notificationTriggerTime - now;

    if (timeUntilNotification > 0) {
      const timeoutId = setTimeout(() => {
        const remainingMinutes = Math.ceil(
          (sessionEndTime - Date.now()) / 60000
        );
        showAlert({
          id: 'session-end-warning',
          type: AlertType.Warning,
          title: 'Session Ending Soon',
          text: `Your streaming session will end in approximately ${remainingMinutes} minutes. Please save your work.`,
        });
      }, timeUntilNotification);

      return () => clearTimeout(timeoutId);
    }
  }, [session, config]);
}
